import React, { useState } from 'react';
import { Phone, Send, Navigation, CheckCircle } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import toast from 'react-hot-toast';

interface BlogInlineLeadFormProps {
  title: string;
}

const BlogInlineLeadForm: React.FC<BlogInlineLeadFormProps> = ({ title }) => {
  const { addInquiry, settings } = useData();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    product: title || '',
    quantity: '',
    quantityUnit: 'kg',
    location: ''
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

    const detectLocation = async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    setIsDetectingLocation(true);
    try {
      const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });

      if (permissionStatus.state === 'denied') {
        toast.error('Location access denied. Please enable it in your browser settings.');
        setIsDetectingLocation(false);
        return;
      }

      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        });
      });
      const { latitude, longitude } = position.coords;
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10`
      );
      const data = await response.json();
      if (data.display_name) {
        setFormData(prev => ({ ...prev, location: data.display_name }));
        toast.success('Location detected successfully!');
      } else {
        toast.error('Could not determine location name');
      }
    } catch (error) {
      console.error('Error detecting location:', error);
      if (typeof error === 'object' && error !== null && 'code' in error && (error as { code: number }).code === 1) {
        toast.error('Location access denied. Please enable location services.');
      } else {
        toast.error('Failed to detect location. Please enter manually.');
      }
    } finally {
      setIsDetectingLocation(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }
    setIsSubmitting(true);
    try {
      const fullQuantity = formData.quantity
        ? `${formData.quantity} ${formData.quantityUnit}`
        : null;

      await addInquiry({
        name: formData.name || '',
        email: formData.email || '',
        phone: formData.phone,
        company: formData.company || '',
        product: formData.product || '',
        quantity: fullQuantity || '',
        location: formData.location || '',
        message: `Blog: ${title}${formData.location ? ` | Location: ${formData.location}` : ''}${
          formData.product ? ` | Product: ${formData.product}` : ''
        }${fullQuantity ? ` | Qty: ${fullQuantity}` : ''}`,
        status: 'new',
        createdAt: new Date().toISOString()
      });

      setIsSubmitted(true);
      toast.success('Thanks! We will contact you shortly.');
    } catch (error) {
      console.error('Error submitting blog inquiry:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-gradient-to-br from-emerald-50 to-green-50 border border-green-200 rounded-2xl p-6 text-center">
        <div className="flex flex-col items-center">
          <CheckCircle className="text-green-600 mb-3" size={40} />
          <h4 className="text-xl font-bold text-gray-900 mb-1">Thank you!</h4>
          <p className="text-gray-700">Your inquiry for “{title}” has been received.</p>
          <a
            href={`tel:${settings.contact.phone.replace(/[^\d+]/g, '')}`}
            className="mt-4 inline-flex items-center justify-center gap-1.5 bg-amber-600 text-white px-3 py-1.5 rounded-md text-sm font-semibold hover:bg-amber-700 transition-colors whitespace-nowrap"
          >
            <Phone size={16} />
            <span>Call Now: {settings.contact.phone}</span>
          </a>
        </div>
      </div>
    );
  }

  return (
    <section aria-label="Get Quote for this article" className="mb-12">
      <div className="bg-white border border-amber-200/50 shadow-sm rounded-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 px-6 py-5 border-b border-amber-200">
          <h3 className="text-xl font-bold text-amber-800">
            Get Best Price for “{title}”
          </h3>
          <p className="text-amber-800/80 text-sm mt-1">
            Share your details to receive quick pricing and availability.
          </p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Call Now card */}
            <div className="order-2 lg:order-1">
              <div className="h-full bg-gradient-to-br from-amber-600 to-yellow-600 text-white rounded-xl p-5">
                <h4 className="text-lg font-bold mb-2">Prefer to talk?</h4>
                <p className="text-white/90 mb-4 text-sm">
                  Call our team for instant assistance and quotes.
                </p>
                <a
                  href={`tel:${settings.contact.phone.replace(/[^\d+]/g, '')}`}
                  className="inline-flex items-center justify-center bg-white text-amber-700 font-semibold py-2 px-3 rounded-md hover:bg-gray-100 transition-colors text-sm whitespace-nowrap"
                >
                  <Phone size={16} className="mr-1.5" />
                  Call Now: {settings.contact.phone}
                </a>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2 order-1 lg:order-2">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      placeholder="e.g. 9876543210"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company/Farm</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
                    <input
                      type="text"
                      name="product"
                      value={formData.product}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      placeholder="Product interested in"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                    <div className="flex space-x-2">
                      <input
                        type="number"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleChange}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                        placeholder="e.g. 100"
                        min={1}
                      />
                      <select
                        name="quantityUnit"
                        value={formData.quantityUnit}
                        onChange={handleChange}
                        className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      >
                        <option value="kg">KG</option>
                        <option value="bags">Bags</option>
                        <option value="tons">Tons</option>
                        <option value="quintals">Quintals</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                      placeholder="City, State"
                    />
                    <button
                      type="button"
                      onClick={detectLocation}
                      disabled={isDetectingLocation || isSubmitting}
                      className="px-4 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                    >
                      {isDetectingLocation ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Navigation size={18} />
                          <span className="hidden sm:inline">Detect</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-amber-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      <span>Get Quote</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogInlineLeadForm;
